import { z } from 'zod'

import { SHWError } from '@/common/error'

import { SAT_BOOTCAMP_PROGRAM_SLUG } from '@/queries/constants'
import {
  COLLEGE_ADMISSIONS_WORKSHOPS_PROGRAM_SLUG,
  DASHBOARD_PROGRAM_SLUGS,
  type DashboardProgramSlug,
  DIALOGUES_PROGRAM_SLUG,
} from '@/client/services/programs/constants'
import {
  getDashboardLiveHelpUrl,
  getDashboardSessionsUrl,
  getDashboardUrl,
  getProgramReturnToUrl,
} from '@/client/services/url'
import { COLLEGE_PREP_WORLD_SLUG, SAT_PREP_WORLD_SLUG } from '@/client/services/worlds/constants'

export enum PrimaryInterestV2Type {
  Learn = 'learn',
  Tutor = 'tutor',
}

export enum PrimaryInterestV2Category {
  SAT = 'sat',
  College = 'college',
  Dialogues = 'dialogues',
  Homework = 'homework',
  Tutor = 'tutor',
}

export enum PrimaryInterestV2Format {
  Program = 'program',
  GroupSessions = 'group-sessions',
  OneOnOneSessions = '1-1-sessions',
  LiveHelp = 'live-help',
}

export enum PrimaryInterestV2Source {
  Onboarding = 'onboarding',
  LandingPage = 'landing-page',
  ProgramPage = 'program-page',
  SATInvitation = 'sat-invitation',
  SATFriendReservedSeries = 'sat-friend-reserved-series',
}

export const PROGRAM_SLUG_TO_INTEREST = {
  [SAT_BOOTCAMP_PROGRAM_SLUG]: PrimaryInterestV2Category.SAT,
  [COLLEGE_ADMISSIONS_WORKSHOPS_PROGRAM_SLUG]: PrimaryInterestV2Category.College,
  [DIALOGUES_PROGRAM_SLUG]: PrimaryInterestV2Category.Dialogues,
} as const

export const PrimaryInterestV2Schema = z.object({
  type: z.nativeEnum(PrimaryInterestV2Type),
  interest: z.nativeEnum(PrimaryInterestV2Category).nullable(),
  format: z.nativeEnum(PrimaryInterestV2Format).nullable(),
  programSlug: z.enum(DASHBOARD_PROGRAM_SLUGS).optional(),
  nonProgramWorldSlug: z.string().nullable(),
  source: z.nativeEnum(PrimaryInterestV2Source),
})

export type PrimaryInterestV2 = z.infer<typeof PrimaryInterestV2Schema>

export function isProgramFormat(
  primaryInterest: PrimaryInterestV2
): primaryInterest is PrimaryInterestV2 & {
  format: PrimaryInterestV2Format.Program
  programSlug: string
} {
  return primaryInterest.format === PrimaryInterestV2Format.Program
}

export const FORMAT_TO_REDIRECT_URL: Record<
  PrimaryInterestV2Format,
  (
    interest: PrimaryInterestV2Category,
    programSlug?: DashboardProgramSlug,
    mode?: PrimaryInterestV2Type
  ) => string
> = {
  [PrimaryInterestV2Format.Program]: (interest, programSlug, mode) => {
    if (!programSlug) {
      throw new SHWError({
        internalMessage: `Program slug is required for Program format with interest: ${interest}`,
      })
    }

    return getProgramReturnToUrl({
      programSlug,
      as: mode === PrimaryInterestV2Type.Tutor ? 'tutor' : 'learner',
    })
  },
  [PrimaryInterestV2Format.LiveHelp]: interest =>
    getDashboardLiveHelpUrl({
      worldSlug: PRIMARY_INTEREST_V2_CATEGORY_CONFIG[interest].nonProgramWorldSlug ?? undefined,
    }),
  [PrimaryInterestV2Format.GroupSessions]: interest =>
    getDashboardSessionsUrl({
      worldSlug: PRIMARY_INTEREST_V2_CATEGORY_CONFIG[interest].nonProgramWorldSlug ?? undefined,
    }),
  [PrimaryInterestV2Format.OneOnOneSessions]: () => getDashboardUrl('live-help'),
}

export const PRIMARY_INTEREST_V2_CATEGORY_CONFIG: Record<
  PrimaryInterestV2Category,
  {
    nonProgramWorldSlug: string | null
    programSlug: DashboardProgramSlug | null
    formats: PrimaryInterestV2Format[]
  }
> = {
  [PrimaryInterestV2Category.SAT]: {
    nonProgramWorldSlug: SAT_PREP_WORLD_SLUG,
    programSlug: SAT_BOOTCAMP_PROGRAM_SLUG,
    formats: [
      PrimaryInterestV2Format.Program,
      PrimaryInterestV2Format.GroupSessions,
      PrimaryInterestV2Format.LiveHelp,
    ],
  },
  [PrimaryInterestV2Category.College]: {
    nonProgramWorldSlug: COLLEGE_PREP_WORLD_SLUG,
    programSlug: COLLEGE_ADMISSIONS_WORKSHOPS_PROGRAM_SLUG,
    formats: [
      PrimaryInterestV2Format.Program,
      PrimaryInterestV2Format.GroupSessions,
      PrimaryInterestV2Format.LiveHelp,
    ],
  },
  [PrimaryInterestV2Category.Dialogues]: {
    nonProgramWorldSlug: null,
    programSlug: DIALOGUES_PROGRAM_SLUG,
    formats: [PrimaryInterestV2Format.Program],
  },
  [PrimaryInterestV2Category.Homework]: {
    nonProgramWorldSlug: null,
    programSlug: null,
    formats: [
      // Live Help Experiment: Send all homework help users to live help. So we don't need them to choose an option.
      // PrimaryInterestV2Format.LiveHelp,
      // PrimaryInterestV2Format.GroupSessions,
      // PrimaryInterestV2Format.OneOnOneSessions,
    ],
  },
  [PrimaryInterestV2Category.Tutor]: {
    nonProgramWorldSlug: null,
    programSlug: null,
    formats: [],
  },
}
